import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { useSearchParams } from 'react-router'

import ActionButton from '@/components/misc/ActionButton'
import { COMPANY_PROFILE_TAB } from '@/components/misc/CompanyProfile/constants'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import useAppDispatch from '@/hooks/useAppDispatch'
import { useInvalidatePanelQueries } from '@/hooks/useInvalidatePanelQueries'
import useNotyf from '@/hooks/useNotyf'
import usePosthogCapture from '@/hooks/usePosthogCapture/usePosthogCapture'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import panelService from '@/lib/services/panelService'
import { setShouldShowFirstRelationshipRequestApprovedModal } from '@/redux/slices/panel'
import { CompanyProfileItemPanel } from '@/types/responses/companies'

interface Props {
  panel: CompanyProfileItemPanel
  onAccept: () => void
  onHide: () => void
}

const AcceptInviteModal: React.FC<Props> = ({ panel, onAccept, onHide }) => {
  const acceptPanelInviteMutation = useAcceptPanelInviteMutation()

  return (
    <Dialog open onHide={onHide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Accept relationship invitation</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="mb-0">
            <p className="mb-4">
              <strong>{panel.fromCompany.presentationName}</strong> has invited
              you to join their professional network.
            </p>

            <p className="mb-0">
              If you accept their invite you will be able to refer clients
              between each other.
            </p>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>

          <ActionButton
            variant="success"
            onClick={() =>
              acceptPanelInviteMutation.mutate(panel, {
                onSuccess: onAccept,
              })
            }
            isProcessing={acceptPanelInviteMutation.isPending}
            isProcessingText="Accepting invite"
          >
            Accept invitation
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function useAcceptPanelInviteMutation() {
  const notyf = useNotyf()
  const invalidatePanelQueries = useInvalidatePanelQueries()
  const dispatch = useAppDispatch()
  const { posthogCapture } = usePosthogCapture()
  const [searchParams, setSearchParams] = useSearchParams()

  return useMutation({
    mutationFn: (panel: CompanyProfileItemPanel) =>
      panelService.acceptPanelInvite(panel.id),

    onSuccess: async (response, panel) => {
      posthogCapture({
        appArea: 'relationship-management',
        action: 'relationship-accepted',
      })

      await invalidatePanelQueries()

      if (response.isFirstPanelApprovedByToCompany) {
        dispatch(setShouldShowFirstRelationshipRequestApprovedModal(true))
      }

      notyf.success(
        `${panel.fromCompany.presentationName} was added to your approved professional network`,
      )

      const needsToReviewAgreements = panel.commercialAgreements.some(
        (agreement) => agreement.needsReview,
      )

      if (needsToReviewAgreements) {
        searchParams.set(
          SEARCH_PARAMS.companyProfile.profileTab,
          COMPANY_PROFILE_TAB.commercialAgreements,
        )
        setSearchParams(searchParams)
      }
    },
    onError: () => {
      notyf.error('Could not accept invite')
    },
  })
}

export default AcceptInviteModal
